import { apiClient } from "@apis/apiClient";

const humanize = (s: string) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Derive a clean analytics URL and page title from the current browser location.
 * Strips numeric-only path segments (entity IDs) so pages group together in analytics,
 * and appends the active tab (if any) as a trailing segment.
 */
export function derivePageInfo(): { url: string; title: string } {
  const pathname =
    window.location.pathname
      .split("/")
      .filter((s) => s && !/^\d+$/.test(s))
      .join("/") || "/";
  const cleanPathname = pathname.startsWith("/") ? pathname : `/${pathname}`;

  const params = new URLSearchParams(window.location.search);
  const tabActive = params.get("tabActive");
  const friendlyTab =
    tabActive?.replace(/_tab$/, "").replace(/^asset_/, "") ?? null;

  const url = friendlyTab ? `${cleanPathname}/${friendlyTab}` : cleanPathname;

  const segments = cleanPathname
    .replace(/^\//, "")
    .split("/")
    .filter(Boolean);
  const pageTitle = segments.map(humanize).join(" - ") || "Home";
  const title = friendlyTab
    ? `${pageTitle} - ${humanize(friendlyTab)}`
    : pageTitle;

  return { url, title };
}

// Umami's /api/send only creates the session row for "event" payloads; an
// "identify" that arrives before the session exists is rejected with a 500
// (STIC-15669). Gate identify behind the first event request so the session
// exists by the time identify is processed.
let firstEventRequest: Promise<unknown> | null = null;

const usageAnalyticsClient = {
  trackPageView(url: string, title: string): void {
    const request = apiClient
      .post("/rest/usage_analytics/event", {
        hostname: window.location.hostname,
        screen: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
        referrer: document.referrer,
        url,
        title,
      })
      .catch(() => {});
    firstEventRequest ??= request;
  },

  trackCustomEvent(
    name: string,
    data?: Record<string, unknown>,
  ): void {
    const request = apiClient
      .post("/rest/usage_analytics/event", {
        hostname: window.location.hostname,
        screen: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
        ...derivePageInfo(),
        name,
        data,
      })
      .catch(() => {});
    firstEventRequest ??= request;
  },

  identify(data: Record<string, unknown>): void {
    (firstEventRequest ?? Promise.resolve())
      .then(() =>
        apiClient.post("/rest/usage_analytics/identify", {
          hostname: window.location.hostname,
          data,
        }),
      )
      .catch(() => {});
  },
};

export default usageAnalyticsClient;
